function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
var _abortController = _classPrivateFieldLooseKey("abortController");
class DeferredMedia extends Component {
  constructor(...args) {
    super(...args);
    /** @type {boolean} */
    this.isPlaying = false;
    Object.defineProperty(this, _abortController, {
      writable: true,
      value: new AbortController()
    });
    /**
     * Shows the deferred media content
     */
    this.showDeferredMedia = () => {
      this.loadContent(true);
      this.isPlaying = true;
      this.updatePlayPauseHint(this.isPlaying);
    };
  }
  connectedCallback() {
    super.connectedCallback();
    const signal = _classPrivateFieldLooseBase(this, _abortController)[_abortController].signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), {
      signal
    });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), {
      signal
    });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    _classPrivateFieldLooseBase(this, _abortController)[_abortController].abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }
  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;
    this.dispatchEvent(new MediaStartedPlayingEvent(this));
    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
    if (!content) return;
    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);
    if (focus && content instanceof HTMLElement) {
      content.focus();
    }
    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }
  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(iframe.dataset.videoType === 'youtube' ? '{"event":"command","func":"playVideo","args":""}' : '{"method":"play"}', '*');
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(iframe.dataset.videoType === 'youtube' ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}' : '{"method":"pause"}', '*');
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // Show poster/play button again when paused (hover-to-play kartlarda play tuşu geri gelsin)
    this.refs.deferredMediaPlayButton?.classList.remove('deferred-media__playing');

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}
if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
var _abortController2 = _classPrivateFieldLooseKey("abortController");
var _waitForModelViewerUI = _classPrivateFieldLooseKey("waitForModelViewerUI");
class ProductModel extends DeferredMedia {
  constructor(...args) {
    super(...args);
    /**
     * Waits for Shopify.ModelViewerUI to be defined.
     * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
     * @returns {Promise<void>}
     */
    Object.defineProperty(this, _waitForModelViewerUI, {
      value: _waitForModelViewerUI2
    });
    Object.defineProperty(this, _abortController2, {
      writable: true,
      value: new AbortController()
    });
  }
  loadContent() {
    super.loadContent();
    Shopify.loadFeatures([{
      name: 'model-viewer-ui',
      version: '1.0',
      onLoad: this.setupModelViewerUI.bind(this)
    }]);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    _classPrivateFieldLooseBase(this, _abortController2)[_abortController2].abort();
  }
  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }
  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;
    if (!Shopify.ModelViewerUI) {
      await _classPrivateFieldLooseBase(this, _waitForModelViewerUI)[_waitForModelViewerUI]();
    }
    if (!Shopify.ModelViewerUI) return;
    const element = this.querySelector('model-viewer');
    if (!element) return;
    const signal = _classPrivateFieldLooseBase(this, _abortController2)[_abortController2].signal;
    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;
    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;
    element.addEventListener('pointerdown', (/** @type {PointerEvent} */event) => {
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
    }, {
      signal
    });
    element.addEventListener('click', (/** @type {PointerEvent} */event) => {
      const distanceX = Math.abs(event.clientX - pointerStartX);
      const distanceY = Math.abs(event.clientY - pointerStartY);
      const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

      // Try to ensure that this is a tap, not a drag.
      if (totalDistance < 10) {
        // When the model is paused, it has its own button overlay for playing the model again.
        // If we're receiving a click event, it means the model is playing, all we can do is pause it.
        this.pauseMedia();
      }
    }, {
      signal
    });
  }
}
async function _waitForModelViewerUI2() {
  const maxAttempts = 10;
  const interval = 50;
  for (let i = 0; i < maxAttempts; i++) {
    if (Shopify.ModelViewerUI) {
      return;
    }
    await new Promise(resolve => setTimeout(resolve, interval));
  }
}
if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
